import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import applicantApi from "@/lib/applicantApi";
import { formatApiErrorDetail } from "@/lib/api";
import { useApplicantAuth } from "@/context/ApplicantAuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft,
  ArrowRight,
  ShieldCheck,
  Lock,
  Upload,
  CheckCircle2,
  FileCheck,
} from "lucide-react";

const STEPS = ["Account", "Vetting", "Work History", "Documents & Consent"];

const emptyWork = () => ({ company: "", supervisor: "", contact: "", dates_start: "", dates_end: "", responsibilities: "" });

const formatSSN = (s) => {
  const d = (s || "").replace(/\D/g, "").slice(0, 9);
  if (d.length <= 3) return d;
  if (d.length <= 5) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
};
const maskSSNDisplay = (s) => {
  const d = (s || "").replace(/\D/g, "").slice(0, 9);
  if (!d) return "";
  const last4 = d.slice(-4);
  const visible = d.length >= 4 ? last4 : d;
  return d.length < 4 ? d : `***-**-${visible}`;
};

const ApplicantApply = () => {
  const nav = useNavigate();
  const { applicant, register, setApplicant, checking } = useApplicantAuth();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);

  // Step 0 — account
  const [acc, setAcc] = useState({ first_name: "", last_name: "", email: "", phone: "", password: "", applied_role: "caregiver" });

  // Step 1 — behavioral + passport
  const [beh, setBeh] = useState({ q1_high_profile: "", q2_distressed_child: "", q3_care_philosophy: "" });
  const [hasPassport, setHasPassport] = useState("no");

  // Step 2 — work history (3 blocks)
  const [work, setWork] = useState([emptyWork(), emptyWork(), emptyWork()]);

  // Step 3 — docs
  const [social, setSocial] = useState({ linkedin: "", instagram: "", facebook: "", other: "" });
  const [ssn, setSsn] = useState("");
  const [ssnFocused, setSsnFocused] = useState(false);
  const [consent, setConsent] = useState(false);
  const [uploads, setUploads] = useState({}); // { kind: { size_bytes, original_filename } }

  useEffect(() => {
    // If already logged in, skip to step 1
    if (applicant && step === 0) {
      setAcc((a) => ({ ...a, first_name: applicant.first_name, last_name: applicant.last_name, email: applicant.email, phone: applicant.phone }));
      // hydrate progressive fields if server has them
      if (applicant.behavioral) setBeh({ ...applicant.behavioral });
      if (applicant.work_history?.length) setWork([...applicant.work_history, ...Array(Math.max(0, 3 - applicant.work_history.length)).fill(emptyWork())].slice(0, 3));
      if (applicant.social) setSocial({ ...applicant.social });
      if (applicant.has_passport) setHasPassport("yes");
      if (applicant.file_ids) setUploads(applicant.file_ids);
      setStep(1);
    }
  }, [applicant]);  // eslint-disable-line

  const percent = useMemo(() => Math.round(((step + 1) / STEPS.length) * 100), [step]);

  const nextStep = () => setStep((s) => Math.min(STEPS.length - 1, s + 1));
  const prevStep = () => setStep((s) => Math.max(0, s - 1));

  // Step 0 submit → register
  const submitAccount = async (e) => {
    e.preventDefault();
    if (!acc.email || acc.password.length < 8 || !acc.first_name || !acc.last_name || !acc.phone) {
      toast.error("Please complete all fields (password ≥ 8 characters).");
      return;
    }
    setBusy(true);
    try {
      await register({
        email: acc.email,
        password: acc.password,
        first_name: acc.first_name,
        last_name: acc.last_name,
        phone: acc.phone,
        applied_role: acc.applied_role,
      });
      toast.success("Account created. Let's continue.");
      setStep(1);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not create account");
    } finally {
      setBusy(false);
    }
  };

  const saveProgress = async ({ silent = false } = {}) => {
    setBusy(true);
    try {
      const payload = {
        behavioral: beh,
        has_passport: hasPassport === "yes",
        work_history: work.filter((w) => (w.company || "").trim() || (w.supervisor || "").trim() || (w.responsibilities || "").trim()),
        social,
      };
      const { data } = await applicantApi.patch("/applicants/me/application", payload);
      setApplicant(data);
      if (!silent) toast.success("Progress saved.");
      return true;
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const uploadFile = async (kind, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("File exceeds 10 MB"); return; }
    if (!["application/pdf", "image/jpeg", "image/png"].includes(file.type)) {
      toast.error("Only PDF, JPEG, or PNG accepted");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("kind", kind);
      fd.append("file", file);
      const { data } = await applicantApi.post("/applicants/me/files", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploads((u) => ({ ...u, [kind]: data.id }));
      toast.success(`${data.original_filename} uploaded securely.`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const finalSubmit = async () => {
    if (!consent) { toast.error("Please acknowledge the compliance notice to continue."); return; }
    if (!/^\d{3}-?\d{2}-?\d{4}$/.test(ssn.replace(/\D/g, "").padStart(0, "0")) && ssn.replace(/\D/g, "").length !== 9) {
      toast.error("Please enter a valid 9-digit SSN.");
      return;
    }
    setBusy(true);
    try {
      // 1) save application with SSN
      await applicantApi.patch("/applicants/me/application", {
        behavioral: beh,
        has_passport: hasPassport === "yes",
        work_history: work.filter((w) => (w.company || "").trim()),
        social,
        ssn_plain: ssn.replace(/\D/g, ""),
        consent_given: true,
      });
      // 2) submit
      const { data } = await applicantApi.post("/applicants/me/submit");
      setApplicant(data);
      toast.success("Application submitted. We'll be in touch shortly.");
      nav("/careers/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Submission failed");
    } finally {
      setBusy(false);
    }
  };

  if (checking) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;

  return (
    <main data-testid="apply-page" className="min-h-screen bg-background pb-20">
      <div className="border-b border-border bg-background/95 backdrop-blur sticky top-0 z-20">
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <Link to="/careers" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary">
            <ArrowLeft size={14} /> Careers
          </Link>
          <div className="text-xs text-muted-foreground">Step {step + 1} of {STEPS.length}: <span className="text-foreground font-medium">{STEPS[step]}</span></div>
        </div>
        <div className="max-w-4xl mx-auto px-6 md:px-10 pb-4">
          <Progress data-testid="apply-progress" value={percent} className="h-2" />
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 md:px-10 pt-10">
        {step === 0 && (
          <form data-testid="step-account" onSubmit={submitAccount} className="rounded-2xl border border-border/70 bg-card p-6 md:p-10">
            <h1 className="font-serif-display text-3xl md:text-4xl">Create your applicant account</h1>
            <p className="mt-3 text-muted-foreground text-sm">You'll use this to save progress, upload documents, and access training once pre-screened.</p>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
              <F label="First name"><Input data-testid="input-first-name" value={acc.first_name} onChange={(e) => setAcc({ ...acc, first_name: e.target.value })} className="h-11 rounded-lg" /></F>
              <F label="Last name"><Input data-testid="input-last-name" value={acc.last_name} onChange={(e) => setAcc({ ...acc, last_name: e.target.value })} className="h-11 rounded-lg" /></F>
              <F label="Email"><Input data-testid="input-account-email" type="email" value={acc.email} onChange={(e) => setAcc({ ...acc, email: e.target.value })} className="h-11 rounded-lg" /></F>
              <F label="Phone"><Input data-testid="input-account-phone" value={acc.phone} onChange={(e) => setAcc({ ...acc, phone: e.target.value })} className="h-11 rounded-lg" /></F>
              <div className="md:col-span-2">
                <F label="Which role are you applying for?">
                  <select
                    data-testid="input-applied-role"
                    value={acc.applied_role}
                    onChange={(e) => setAcc({ ...acc, applied_role: e.target.value })}
                    className="h-11 w-full rounded-lg border border-border bg-background px-3 text-sm"
                  >
                    <option value="caregiver">Caregiver</option>
                    <option value="care_coordinator">Care Coordinator</option>
                    <option value="care_supervisor">Care Supervisor</option>
                    <option value="care_concierge">Care Concierge</option>
                    <option value="admin_hr">Admin / HR</option>
                  </select>
                </F>
                <p className="mt-1 text-[11px] text-muted-foreground">Your training modules are assigned based on the role you apply for.</p>
              </div>
              <div className="md:col-span-2">
                <F label="Password (≥ 8 characters)">
                  <Input data-testid="input-account-password" type="password" value={acc.password} onChange={(e) => setAcc({ ...acc, password: e.target.value })} className="h-11 rounded-lg" autoComplete="new-password" />
                </F>
              </div>
            </div>
            <div className="mt-8 flex justify-end">
              <Button type="submit" data-testid="btn-create-account" disabled={busy} className="rounded-full h-12 px-7 bg-primary text-primary-foreground">
                {busy ? "Creating…" : (<>Create account & continue <ArrowRight size={15} className="ml-1.5" /></>)}
              </Button>
            </div>
            <p className="mt-6 text-xs text-muted-foreground">Already have an account? <Link className="text-primary hover:underline" to="/careers/login">Sign in</Link></p>
          </form>
        )}

        {step === 1 && (
          <div data-testid="step-vetting" className="rounded-2xl border border-border/70 bg-card p-6 md:p-10 space-y-8">
            <div>
              <div className="text-xs tracking-[0.25em] uppercase text-primary/80">Baseline Vetting</div>
              <h1 className="mt-3 font-serif-display text-3xl md:text-4xl">Tell us how you think about care.</h1>
              <p className="mt-3 text-muted-foreground text-sm">We use short-answer responses to understand your premium-care philosophy. Aim for 3–6 sentences per question.</p>
            </div>
            <div className="space-y-6">
              <F label="Q1 — Handling a high-profile client environment">
                <Textarea data-testid="input-q1" rows={4} value={beh.q1_high_profile} onChange={(e) => setBeh({ ...beh, q1_high_profile: e.target.value })} placeholder="Describe your approach to discretion, dress code, and family boundaries." className="rounded-lg resize-none" />
              </F>
              <F label="Q2 — Soothing a distressed child in a busy, unfamiliar venue">
                <Textarea data-testid="input-q2" rows={4} value={beh.q2_distressed_child} onChange={(e) => setBeh({ ...beh, q2_distressed_child: e.target.value })} placeholder="Walk us through your exact steps." className="rounded-lg resize-none" />
              </F>
              <F label="Q3 — Your premium care philosophy in your own words">
                <Textarea data-testid="input-q3" rows={4} value={beh.q3_care_philosophy} onChange={(e) => setBeh({ ...beh, q3_care_philosophy: e.target.value })} placeholder="What sets your care apart?" className="rounded-lg resize-none" />
              </F>
              <F label="Do you hold a valid, unexpired US Passport? (required for select international/travel assignments)">
                <Select value={hasPassport} onValueChange={(v) => setHasPassport(v)}>
                  <SelectTrigger data-testid="select-passport" className="h-11 rounded-lg"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no">No</SelectItem>
                    <SelectItem value="yes">Yes</SelectItem>
                  </SelectContent>
                </Select>
              </F>
              {hasPassport === "yes" && (
                <div data-testid="passport-upload-block" className="rounded-xl border border-primary/20 bg-primary/5 p-5">
                  <div className="text-xs uppercase tracking-widest text-primary flex items-center gap-2 mb-3"><Lock size={12} /> Secure upload · Passport data page</div>
                  <SecureFileInput kind="passport" uploads={uploads} onFile={(f) => uploadFile("passport", f)} />
                </div>
              )}
            </div>
            <NavRow busy={busy} onPrev={prevStep} onNext={async () => { if (await saveProgress({ silent: true })) nextStep(); }} showPrev />
          </div>
        )}

        {step === 2 && (
          <div data-testid="step-work" className="rounded-2xl border border-border/70 bg-card p-6 md:p-10 space-y-8">
            <div>
              <div className="text-xs tracking-[0.25em] uppercase text-primary/80">Verifiable Work History</div>
              <h1 className="mt-3 font-serif-display text-3xl md:text-4xl">Three verifiable caregiving positions.</h1>
              <p className="mt-3 text-muted-foreground text-sm">Roles held within the last 5–10 years. Each reference will be independently contacted.</p>
            </div>
            {work.map((w, idx) => (
              <div key={idx} data-testid={`work-block-${idx}`} className="rounded-xl border border-border/70 p-5 md:p-6 bg-muted/30 space-y-4">
                <div className="text-xs tracking-widest uppercase text-muted-foreground">Position {idx + 1}</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <F label="Company / Family name"><Input data-testid={`input-work-company-${idx}`} value={w.company} onChange={(e) => setWork((ws) => ws.map((x, i) => i === idx ? { ...x, company: e.target.value } : x))} className="h-11 rounded-lg" /></F>
                  <F label="Supervisor name"><Input data-testid={`input-work-supervisor-${idx}`} value={w.supervisor} onChange={(e) => setWork((ws) => ws.map((x, i) => i === idx ? { ...x, supervisor: e.target.value } : x))} className="h-11 rounded-lg" /></F>
                  <F label="Contact phone / email"><Input data-testid={`input-work-contact-${idx}`} value={w.contact} onChange={(e) => setWork((ws) => ws.map((x, i) => i === idx ? { ...x, contact: e.target.value } : x))} className="h-11 rounded-lg" /></F>
                  <div className="grid grid-cols-2 gap-3">
                    <F label="From (YYYY-MM)"><Input data-testid={`input-work-start-${idx}`} value={w.dates_start} onChange={(e) => setWork((ws) => ws.map((x, i) => i === idx ? { ...x, dates_start: e.target.value } : x))} placeholder="2022-03" className="h-11 rounded-lg" /></F>
                    <F label="To (YYYY-MM)"><Input data-testid={`input-work-end-${idx}`} value={w.dates_end} onChange={(e) => setWork((ws) => ws.map((x, i) => i === idx ? { ...x, dates_end: e.target.value } : x))} placeholder="2024-11" className="h-11 rounded-lg" /></F>
                  </div>
                  <div className="md:col-span-2">
                    <F label="Roles / responsibilities">
                      <Textarea data-testid={`input-work-resp-${idx}`} rows={3} value={w.responsibilities} onChange={(e) => setWork((ws) => ws.map((x, i) => i === idx ? { ...x, responsibilities: e.target.value } : x))} className="rounded-lg resize-none" />
                    </F>
                  </div>
                </div>
              </div>
            ))}
            <NavRow busy={busy} onPrev={prevStep} onNext={async () => { if (await saveProgress({ silent: true })) nextStep(); }} showPrev />
          </div>
        )}

        {step === 3 && (
          <div data-testid="step-docs" className="rounded-2xl border border-border/70 bg-card p-6 md:p-10 space-y-8">
            <div>
              <div className="text-xs tracking-[0.25em] uppercase text-primary/80">Identity, Documents & Consent</div>
              <h1 className="mt-3 font-serif-display text-3xl md:text-4xl">The final verification step.</h1>
              <p className="mt-3 text-muted-foreground text-sm">Every document is encrypted at rest with AES-256 and only decrypted by our HR team for vetting.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <SecureFileCard kind="cpr_cert" title="Current CPR & First Aid Certificate" uploads={uploads} onFile={(f) => uploadFile("cpr_cert", f)} />
              <SecureFileCard kind="livescan" title="Livescan Background Check Document" uploads={uploads} onFile={(f) => uploadFile("livescan", f)} />
              <SecureFileCard kind="gov_id" title="Government-Issued Photo ID" uploads={uploads} onFile={(f) => uploadFile("gov_id", f)} />
              {hasPassport === "yes" && (
                <SecureFileCard kind="passport" title="US Passport (data page)" uploads={uploads} onFile={(f) => uploadFile("passport", f)} />
              )}
            </div>

            {/* SSN — masked */}
            <div className="rounded-xl border border-primary/20 bg-primary/5 p-5 md:p-6">
              <div className="text-xs uppercase tracking-widest text-primary flex items-center gap-2 mb-3"><Lock size={12} /> Social Security Number · Encrypted on submit</div>
              <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end">
                <F label="SSN (masked while you type)">
                  <Input
                    data-testid="input-ssn"
                    inputMode="numeric"
                    value={ssnFocused ? formatSSN(ssn) : (maskSSNDisplay(ssn) || "")}
                    onFocus={() => setSsnFocused(true)}
                    onBlur={() => setSsnFocused(false)}
                    onChange={(e) => setSsn(e.target.value.replace(/\D/g, "").slice(0, 9))}
                    placeholder="***-**-####"
                    className="h-11 rounded-lg font-mono tracking-wider"
                    autoComplete="off"
                  />
                </F>
                <div className="text-xs text-muted-foreground pb-3">Preview: <span data-testid="ssn-mask-preview" className="font-mono text-foreground">{maskSSNDisplay(ssn) || "***-**-####"}</span></div>
              </div>
            </div>

            {/* Social */}
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Social Media Handles · for digital footprint vetting</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <F label="LinkedIn"><Input data-testid="input-social-linkedin" value={social.linkedin} onChange={(e) => setSocial({ ...social, linkedin: e.target.value })} placeholder="https://linkedin.com/in/..." className="h-11 rounded-lg" /></F>
                <F label="Instagram"><Input data-testid="input-social-instagram" value={social.instagram} onChange={(e) => setSocial({ ...social, instagram: e.target.value })} placeholder="@handle" className="h-11 rounded-lg" /></F>
                <F label="Facebook"><Input data-testid="input-social-facebook" value={social.facebook} onChange={(e) => setSocial({ ...social, facebook: e.target.value })} className="h-11 rounded-lg" /></F>
                <F label="Other"><Input data-testid="input-social-other" value={social.other} onChange={(e) => setSocial({ ...social, other: e.target.value })} className="h-11 rounded-lg" /></F>
              </div>
            </div>

            {/* Consent */}
            <div className="rounded-xl border border-border/70 bg-muted/30 p-5 md:p-6">
              <div className="flex items-start gap-3">
                <Checkbox data-testid="checkbox-consent" checked={consent} onCheckedChange={(v) => setConsent(!!v)} className="mt-1" />
                <label className="text-sm text-muted-foreground leading-relaxed">
                  I understand and acknowledge that the personal data and documents I submit — including my Social Security Number, Government ID, Livescan, and Passport (if provided) — are collected <span className="text-foreground font-medium">solely for employment vetting and background checks</span> in compliance with applicable state labor laws. My information is encrypted at rest, restricted to authorised Care Anywhere HR personnel, and will not be shared with third parties except as required by law or with my explicit consent.
                </label>
              </div>
            </div>

            <NavRow
              busy={busy}
              onPrev={prevStep}
              onNext={finalSubmit}
              nextLabel={<>Submit application <CheckCircle2 size={16} className="ml-1.5" /></>}
              testidNext="btn-submit-application"
              showPrev
            />
          </div>
        )}
      </div>
    </main>
  );
};

const F = ({ label, children }) => (
  <div className="flex flex-col gap-2">
    <Label className="text-xs uppercase tracking-widest text-muted-foreground">{label}</Label>
    {children}
  </div>
);

const NavRow = ({ busy, onPrev, onNext, nextLabel, showPrev, testidNext }) => (
  <div className="flex justify-between pt-4">
    {showPrev ? (
      <Button variant="outline" onClick={onPrev} className="rounded-full" data-testid="btn-prev-step">
        <ArrowLeft size={14} className="mr-1.5" /> Back
      </Button>
    ) : <div />}
    <Button onClick={onNext} disabled={busy} data-testid={testidNext || "btn-next-step"} className="rounded-full h-11 px-6 bg-primary text-primary-foreground">
      {busy ? "Working…" : (nextLabel || (<>Save & continue <ArrowRight size={14} className="ml-1.5" /></>))}
    </Button>
  </div>
);

const SecureFileInput = ({ kind, uploads, onFile }) => {
  const uploaded = uploads?.[kind];
  return (
    <label className="flex items-center gap-3 cursor-pointer">
      <div className={`inline-flex items-center gap-2 h-11 rounded-full px-5 border ${uploaded ? "bg-primary/10 border-primary/30 text-primary" : "border-primary/30 text-primary hover:bg-primary/5"}`}>
        {uploaded ? <FileCheck size={15} /> : <Upload size={15} />}
        {uploaded ? "Uploaded — replace" : "Upload PDF/JPEG/PNG"}
      </div>
      <input
        type="file"
        data-testid={`file-${kind}`}
        accept="application/pdf,image/jpeg,image/png"
        className="hidden"
        onChange={(e) => onFile(e.target.files?.[0])}
      />
    </label>
  );
};

const SecureFileCard = ({ kind, title, uploads, onFile }) => (
  <div className="rounded-xl border border-border/70 p-5 bg-background">
    <div className="flex items-start justify-between gap-3">
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-1.5"><ShieldCheck size={12} className="text-primary" /> Secure upload</div>
        <div className="mt-1 font-medium text-foreground text-sm">{title}</div>
        <div className="text-xs text-muted-foreground mt-1">PDF, JPEG or PNG · up to 10MB · AES-256 at rest</div>
      </div>
      {uploads?.[kind] && <CheckCircle2 size={20} className="text-primary shrink-0" data-testid={`file-check-${kind}`} />}
    </div>
    <div className="mt-4">
      <SecureFileInput kind={kind} uploads={uploads} onFile={onFile} />
    </div>
  </div>
);

export default ApplicantApply;
